import { useEffect, useState } from "react";
import { io } from "socket.io-client";
import { Socket } from "socket.io-client";
import { toast } from "@/hooks/use-toast";
import { useCustomToast } from "@/hooks/useCustomToast";
import { queryClient } from "@/lib/queryClient";
import { useAppStore } from "@/store/AppStore";
import { getWebSocketUrl } from "@/config/runtime-env";
import type { Notification } from "@/types";
import { chatEventBridge } from "@/utils/chatEventBridge";
import type { ChatMessageNotification } from "@/components/ui/ChatMessageToast";

const useNotifications = (token: string) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { state, setUnreadCount, incrementUnreadCount } = useAppStore();
  const { showChatMessageToast } = useCustomToast();

  useEffect(() => {
    const websocketUrl = getWebSocketUrl();

    // Connect to notification namespace
    const notificationSocket = io(`${websocketUrl}/notification`, {
      extraHeaders: { Authorization: `Bearer ${token}` },
      query: { token },
      auth: { token },
      reconnectionAttempts: 5,
      transports: ["websocket", "polling"],
    });

    // Connection handlers
    notificationSocket.on("connect", () => {
      console.log("Connected to notifications:", notificationSocket.id);
    });

    notificationSocket.on("connected", (data) => {
      console.log("Notification connection confirmed for user:", data.userId);
      // Request initial unread count
      notificationSocket.emit("get_unread_count");
    });

    // Listen for new notifications
    notificationSocket.on("new_notification", (notification) => {
      console.log("New notification received:", notification);
      setNotifications((prev) => [...prev, notification]);

      // Increment unread count in context store
      incrementUnreadCount();

      // **CUSTOM TOAST: Handle chat message notifications with custom toast**
      if (notification.type === "chat_message" && notification.metadata) {
        console.log("🎯 Showing custom chat message toast");

        // Show custom chat message toast with navigation
        showChatMessageToast(notification as ChatMessageNotification);

        // **BRIDGE: Handle chat message notifications**
        console.log("🌉 Bridging chat_message notification to chat UI update");
        const { sessionId, senderId, senderName, messagePreview, messageType } = notification.metadata;

        // Emit chat update event via the bridge
        chatEventBridge.emit('chat_message_received', {
          sessionId,
          senderId,
          senderName,
          message: messagePreview,
          messageType: messageType || 'text',
          sentAt: notification.createdAt || new Date().toISOString()
        });
      } else {
        // Show regular toast for non-chat notifications
        toast({
          title: notification.title,
          description: notification.message,
        });
      }

      // Invalidate all notification queries to ensure all tabs get updated
      // This covers all filter variations: all, unread, read
      queryClient.invalidateQueries({
        queryKey: ["notifications"],
        exact: false, // This ensures all queries starting with "notifications" are invalidated
      });

      // Show browser notification if permitted
      if (Notification.permission === "granted") {
        new Notification(notification.title, {
          body: notification.message,
          icon: "/notification-icon.png",
        });
      }
    });

    // Listen for unread count updates
    notificationSocket.on("unread_count", (data) => {
      setUnreadCount(data.count);
    });

    notificationSocket.on("unread_count_update", (data) => {
      setUnreadCount(data.count);
    });

    // Listen for errors
    notificationSocket.on("error", (error) => {
      console.error("Notification error:", error);
    });

    setSocket(notificationSocket);

    return () => {
      notificationSocket.disconnect();
    };
  }, [token]);

  return { socket, notifications, unreadCount: state.unreadCount };
};

export default useNotifications;
